import type { EmailDeliveryStatus, EmailLogEntry } from '../types';

export type { EmailDeliveryStatus };

export type EmailLogDeliveryInfo = {
  status: EmailDeliveryStatus;
  label: string;
  shortError: string | null;
  quoteId: string | null;
  canResend: boolean;
};

const QUOTE_ID_RE = /Quote reference:\s*(q_[a-z0-9_]+)/i;

export function extractQuoteIdFromPreview(preview: string): string | null {
  return QUOTE_ID_RE.exec(preview)?.[1] ?? null;
}

/** Body text without trailing delivery footers (lines starting with —). */
export function stripEmailLogPreviewBody(preview: string): string {
  const idx = preview.search(/\n\n—\s/);
  return (idx >= 0 ? preview.slice(0, idx) : preview).trim();
}

function extractDeliveryError(preview: string): string | null {
  const notDelivered = /Email not delivered:\s*(.+?)(?:\n|$)/i.exec(preview);
  if (notDelivered) return notDelivered[1]!.trim();
  const failedBefore = /FAILED before all variants were sent:\s*(.+?)(?:\n|$)/i.exec(preview);
  if (failedBefore) return failedBefore[1]!.trim();
  const generic = /not delivered:\s*(.+?)(?:\n|$)/i.exec(preview);
  if (generic) return generic[1]!.trim();
  return null;
}

export function classifyDeliveryError(message: string): string {
  const m = message.toLowerCase();
  if (/401|403|unauthorized|authentication|invalid credentials|auth/i.test(m)) return 'Unauthorized / SMTP login failed';
  if (/timeout|etimedout|etimeout/i.test(m)) return 'Connection timed out';
  if (/enotfound|dns|getaddrinfo/i.test(m)) return 'Mail server not found (DNS)';
  if (/certificate|tls|ssl/i.test(m)) return 'TLS / certificate error';
  if (/550|554|rejected|spam/i.test(m)) return 'Message rejected by mail server';
  return 'Delivery failed';
}

function parseFromPreview(entry: EmailLogEntry): EmailLogDeliveryInfo {
  const preview = entry.preview;
  const quoteId = entry.quoteId ?? extractQuoteIdFromPreview(preview);
  const rawError = entry.errorMessage ?? extractDeliveryError(preview);

  if (/FAILED before all variants were sent/i.test(preview)) {
    return {
      status: 'partial',
      label: 'Partially sent',
      shortError: rawError ? classifyDeliveryError(rawError) : 'Later step failed',
      quoteId,
      canResend: true,
    };
  }

  if (/Email not delivered:|not delivered:/i.test(preview) || rawError) {
    const err = rawError ?? 'Unknown error';
    return {
      status: 'failed',
      label: classifyDeliveryError(err),
      shortError: err,
      quoteId,
      canResend: true,
    };
  }

  if (
    /Sent via SMTP/i.test(preview) ||
    /Email \d+ of \d+:/i.test(preview) ||
    (/Ticket email:/i.test(preview) && /View ticket \+ View invoice/i.test(preview))
  ) {
    return {
      status: 'sent',
      label: 'Delivered',
      shortError: null,
      quoteId,
      canResend: entry.kind === 'ticket_receipt' || entry.kind === 'quote' || entry.kind === 'oaf' || entry.kind === 'payment_reminder',
    };
  }

  if (
    (entry.kind === 'quote' && /Quote email:|Review & book/i.test(preview)) ||
    (entry.kind === 'oaf' && /OAF email:|Complete payment/i.test(preview))
  ) {
    if (/not delivered/i.test(preview)) {
      return {
        status: 'logged',
        label: 'Logged only',
        shortError: 'Not sent via SMTP',
        quoteId,
        canResend: true,
      };
    }
    return {
      status: 'sent',
      label: 'Delivered',
      shortError: null,
      quoteId,
      canResend: true,
    };
  }

  return {
    status: 'logged',
    label: 'Logged only',
    shortError: entry.kind === 'welcome' ? 'Demo log — not sent via SMTP' : 'Not sent via SMTP',
    quoteId,
    canResend: true,
  };
}

export function resolveEmailLogDelivery(entry: EmailLogEntry): EmailLogDeliveryInfo {
  const quoteId = entry.quoteId ?? extractQuoteIdFromPreview(entry.preview);

  if (entry.deliveryStatus) {
    const shortError = entry.errorMessage ?? null;
    const label =
      entry.deliveryStatus === 'sent'
        ? 'Delivered'
        : entry.deliveryStatus === 'failed'
          ? shortError
            ? classifyDeliveryError(shortError)
            : 'Not sent'
          : entry.deliveryStatus === 'partial'
            ? 'Partially sent'
            : 'Logged only';
    return {
      status: entry.deliveryStatus,
      label,
      shortError,
      quoteId,
      canResend: entry.deliveryStatus !== 'sent' || ['ticket_receipt', 'quote', 'payment_reminder', 'test'].includes(entry.kind),
    };
  }

  return parseFromPreview(entry);
}

export function formatEmailLogSuccessFooter(from: string, messageId?: string): string {
  return `\n\n— Sent via SMTP from ${from}${messageId ? ` (${messageId})` : ''}`;
}

export function formatEmailLogFailureFooter(error: string): string {
  return `\n\n— Email not delivered: ${error}`;
}
